import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useTutorial } from '../contexts/TutorialContext';
import { useAuth } from '../contexts/AuthContext';
import { useAPI } from '../contexts/APIContext';
import logger from '../utils/logger';

const Tutorial = () => {
  const navigate = useNavigate();
  const { showTutorial, currentTutorialStep, isLastTutorialStep, skipTutorial, nextTutorialStep, completeTutorial } = useTutorial();
  const { user } = useAuth();
  const { apiClient } = useAPI();
  const [defaultCharacter, setDefaultCharacter] = useState(null);
  const [spotlightTarget, setSpotlightTarget] = useState(null);
  const [positionVersion, setPositionVersion] = useState(0); // Force re-render on scroll/resize
  const [showDrawHighlight, setShowDrawHighlight] = useState(false); // For delayed Draw card highlight
  const [toolTargets, setToolTargets] = useState({ color: null, brush: null, undo: null }); // For step 7 multiple targets

  // Debug logging for production
  console.log('[Tutorial] Component render:', { showTutorial, currentTutorialStep });

  // Fetch user's first character (Zippy)
  useEffect(() => {
    const fetchDefaultCharacter = async () => {
      if (showTutorial && user) {
        try {
          const response = await apiClient.getUserCharacters(user.id);
          if (response.status && response.data && response.data.length > 0) {
            // Get the first character (Zippy - the default tiger)
            setDefaultCharacter(response.data[0]);
            logger.log('🐯 Found default character:', response.data[0]);
          }
        } catch (error) {
          logger.error('❌ Error fetching default character:', error);
        }
      }
    };

    fetchDefaultCharacter();
  }, [showTutorial, user, apiClient]);

  // Delayed Draw card highlight on step 6 (single setTimeout, no polling)
  useEffect(() => {
    if (currentTutorialStep === 6) {
      setShowDrawHighlight(false);
      const timer = setTimeout(() => setShowDrawHighlight(true), 1000);
      return () => clearTimeout(timer);
    }
    setShowDrawHighlight(false);
  }, [currentTutorialStep]);

  // Spotlight target: run when step or draw highlight changes (no setInterval)
  // UI advances steps by calling nextTutorialStep() / advanceStep() explicitly
  useEffect(() => {
    const findTarget = () => {
      let target = null;

      switch (currentTutorialStep) {
        case 4:
          // Navigation "Create" button
          const buttons = Array.from(document.querySelectorAll('button'));
          target = buttons.find(btn => btn.textContent.trim() === 'Create');
          break;
        case 5:
          // "Create Character" card
          const cards = Array.from(document.querySelectorAll('[class*="group"]'));
          target = cards.find(card => {
            const text = card.textContent;
            return text && text.includes('Create Character');
          });
          break;
        case 6:
          // "Draw" card - only show highlight after 1 second
          if (showDrawHighlight) {
            const drawCards = Array.from(document.querySelectorAll('[class*="group"]'));
            target = drawCards.find(card => {
              const text = card.textContent;
              return text && text.includes('Draw') && text.includes('Sketch your character');
            });
          }
          break;
        case 8:
          // Find all 3 tool targets at once
          const colorPicker = document.querySelector('input[type="color"]') ||
                   Array.from(document.querySelectorAll('button')).find(btn => {
                     const style = window.getComputedStyle(btn);
                     return style.backgroundColor === 'rgb(0, 0, 0)' || style.borderRadius.includes('50%');
                   });
          const brushSlider = document.querySelector('input[type="range"]');
          const allButtons = Array.from(document.querySelectorAll('button'));
          const undoButton = allButtons.find(btn =>
            btn.innerHTML.includes('←') || btn.title?.toLowerCase().includes('undo')
          );
          const undoParent = undoButton?.parentElement;

          setToolTargets({
            color: colorPicker,
            brush: brushSlider,
            undo: undoParent
          });
          target = null; // No single spotlight target for this step
          break;
        case 9:
          // "Create" button at bottom right (in toolbar)
          const createButtons = Array.from(document.querySelectorAll('button'));
          target = createButtons.find(btn => btn.textContent.trim() === 'Create' && btn.closest('button'));
          break;
        case 11:
          // "Save" button for saving the character
          const saveButtons = Array.from(document.querySelectorAll('button'));
          target = saveButtons.find(btn => btn.textContent.trim() === 'Save');
          break;
        case 13:
          // Storybooks button in navigation
          target = document.querySelector('[data-tutorial="storybook-nav"]');
          if (!target) {
            const navButtons = Array.from(document.querySelectorAll('button'));
            target = navButtons.find((btn) =>
              btn.textContent?.trim()?.toLowerCase().includes('storybooks')
            ) || null;
          }
          break;
        case 15:
          // "Create" button on Storybook Library page
          if (window.location.pathname.startsWith('/audiobooks')) {
            target = document.querySelector('[data-tutorial="storybook-create"]');
            if (!target) {
              // Scope fallback search to Storybook Library content area to avoid navbar Create button.
              const libraryHeading = Array.from(document.querySelectorAll('h1')).find((heading) =>
                heading.textContent?.trim()?.toLowerCase().includes('storybook library')
              );
              const libraryContainer =
                libraryHeading?.closest('div.relative.z-10.max-w-7xl.mx-auto') ||
                libraryHeading?.closest('div');
              const scopedButtons = Array.from(
                (libraryContainer || document).querySelectorAll('button')
              );

              target =
                scopedButtons.find((btn) => {
                  const text = btn.textContent?.trim()?.toLowerCase() || '';
                  const className = String(btn.className || '');
                  const plusIcon = btn.querySelector('svg path[d="M12 4v16m8-8H4"]');
                  return (
                    text.includes('create') &&
                    className.includes('from-purple-500') &&
                    className.includes('to-indigo-600') &&
                    !!plusIcon
                  );
                }) ||
                scopedButtons.find((btn) => {
                  const text = btn.textContent?.trim()?.toLowerCase() || '';
                  const className = String(btn.className || '');
                  return (
                    text.includes('create') &&
                    className.includes('from-purple-500') &&
                    className.includes('to-indigo-600')
                  );
                }) ||
                null;
            }
          }
          break;
        case 16:
          // "Read Story" / open story target on Storybook Library
          if (window.location.pathname.startsWith('/audiobooks')) {
            const readButtons = Array.from(document.querySelectorAll('button'));
            target = readButtons.find(btn => {
              const text = btn.textContent?.trim()?.toLowerCase();
              return text?.includes('read story') || text?.includes('open story') || text?.includes('read');
            }) || null;

            // Fallback: storybook card itself is the open action
            if (!target) {
              const storybookCards = Array.from(
                document.querySelectorAll('[data-tutorial="storybook-card"]')
              );
              const fallbackCards = Array.from(
                document.querySelectorAll('[class*="group"][class*="cursor-pointer"]')
              );
              target = storybookCards[0] || fallbackCards[0] || null;
            }
          }
          break;
        default:
          target = null;
          setToolTargets({ color: null, brush: null, undo: null });
      }

      logger.log(`🎯 Looking for spotlight target (step ${currentTutorialStep}):`, target);
      setSpotlightTarget(target);
    };

    const timer = setTimeout(findTarget, 100);

    // Re-calculate spotlight position on scroll or resize
    const handleUpdate = () => {
      findTarget();
      // Force re-render to recalculate spotlight position
      setPositionVersion(prev => prev + 1);
    };

    window.addEventListener('scroll', handleUpdate, true); // Use capture to catch all scrolls
    window.addEventListener('resize', handleUpdate);

    return () => {
      clearTimeout(timer);
      window.removeEventListener('scroll', handleUpdate, true);
      window.removeEventListener('resize', handleUpdate);
    };
  }, [currentTutorialStep, showDrawHighlight]);

  if (!showTutorial) return null;

  // Calculate spotlight position and dimensions
  const getSpotlightStyle = () => {
    if (!spotlightTarget) return null;

    const rect = spotlightTarget.getBoundingClientRect();
    return {
      top: rect.top - 8,
      left: rect.left - 8,
      width: rect.width + 16,
      height: rect.height + 16,
    };
  };

  const spotlightStyle = getSpotlightStyle();

  // Get speech bubble content based on current step
  const getSpeechBubbleContent = () => {
    switch (currentTutorialStep) {
      case 1:
        return {
          title: "Welcome to Pixley!",
          message: "Ready for a quick tour? I'll show you how to create amazing characters and stories!",
          showNext: true,
          showSkip: true,
        };
      case 2:
        return {
          title: "Explore the Storyverse! 🌟",
          message: "This is the Storyverse, where you can watch stories created by other users. Everything here is free and educational!",
          showNext: true,
          showSkip: true,
        };
      case 3:
        return {
          title: "Meet Your First Character!",
          message: `This is ${defaultCharacter?.name || 'Zippy'}, your first character! Now let's learn how to create more characters.`,
          showNext: true,
          showSkip: true,
          character: defaultCharacter,
        };
      case 4:
        return {
          title: "Create New Characters",
          message: "Click the 'Create' button to start making your own unique characters!",
          showNext: false, // User must click the Create button
          showSkip: true,
        };
      case 5:
        return {
          title: "Choose Create Character",
          message: "Click on 'Create Character' to start building your first character!",
          showNext: false, // User must click Create Character card
          showSkip: true,
        };
      case 6:
        return {
          title: "Two Ways to Create",
          message: "You can either Draw your character from scratch or Upload an existing image. Let's try drawing!",
          showNext: false, // User must click Draw card after 1 second
          showSkip: true,
        };
      case 7:
        return {
          title: "Drawing Tools",
          message: "Let's explore the drawing tools! You can change colors, adjust brush size, and undo/redo your strokes.",
          showNext: true,
          showSkip: true,
        };
      case 8:
        return {
          title: "Drawing Tools",
          message: null, // We'll show 3 separate text boxes instead
          showNext: true,
          showSkip: true,
          multiToolStep: true, // Flag to show 3 tool boxes
        };
      case 9:
        return {
          title: "Draw Your Character",
          message: "Now draw your character! When you're done, press the 'Create' button at the bottom right, then click Next below.",
          showNext: true,
          showSkip: true,
        };
      case 10:
        return {
          title: "Creating Your Character...",
          message: "Great! Your character is being generated with AI. This usually takes 20-30 seconds. When you see the Save button, click Next to continue.",
          showNext: true,
          showSkip: true,
        };
      case 11:
        return {
          title: "Character Options",
          message: "Amazing! Your character is ready. Click Save, then give your character a name. When you're done, click Next below.",
          showNext: true,
          showSkip: true,
        };
      case 12:
        return {
          title: "Pixley Stars ⭐",
          message: "Give your character a name, then let's talk about Pixley Stars! Stars power everything you create — characters cost 25 stars and stories cost 50 to 600 depending on length. When you're ready, click Next.",
          showNext: true,
          showSkip: true,
        };
      case 13:
        return {
          title: "Explore Storybooks 📚",
          message: "Let's explore Storybooks! Click the Storybooks button in the navigation to see the stories you create.",
          showNext: false,
          showSkip: true,
        };
      case 14:
        return {
          title: "Welcome to Storybooks 📚",
          message: "Storybooks is where you can read stories created using your Pixley characters. Let's take a quick tour!",
          showNext: true,
          showSkip: true,
        };
      case 15:
        return {
          title: "Create Another Story!",
          message: "You can always create a new story from here. Click the Create button to start another adventure.",
          showNext: false,
          showSkip: true,
        };
      case 16:
        return {
          title: "Open a Storybook",
          message: "Click this card (or Read Story button) to open your storybook and start reading!",
          showNext: false,
          showSkip: false,
          showComplete: true,
        };
      default:
        return null;
    }
  };

  const content = getSpeechBubbleContent();
  if (!content) return null;

  // Calculate dynamic position for Panda and speech bubble based on step
  const getSpeechBubblePosition = () => {
    // For step 9 (Draw), position in top-right with smaller Panda, below menu bar
    if (currentTutorialStep === 9) {
      return {
        top: '100px',
        right: '40px',
        left: 'auto',
      };
    }

    // For step 10 (Creating character), same position as step 9 - off the canvas
    if (currentTutorialStep === 10) {
      return {
        top: '100px',
        right: '40px',
        left: 'auto',
      };
    }

    // For step 11 (Character Options), same position as steps 9 and 10 - off the canvas
    if (currentTutorialStep === 11) {
      return {
        top: '100px',
        right: '40px',
        left: 'auto',
      };
    }

    // For step 12 (Naming modal with Pixley Stars), position at bottom-right to not cover modal
    if (currentTutorialStep === 12) {
      return {
        bottom: '295px',
        right: '55px',
        left: 'auto',
        top: 'auto',
      };
    }

    // For step 13 (Create a Story), position at mid-right like other compact steps
    if (currentTutorialStep === 13) {
      return {
        top: '300px',
        right: '40px',
        left: 'auto',
      };
    }

    // For step 15 (Storybooks Create), place bubble to the LEFT of spotlight target
    if (currentTutorialStep === 15 && spotlightTarget) {
      const rect = spotlightTarget.getBoundingClientRect();
      const gap = 24; // keep 20–30px spacing from spotlight
      const estimatedBubbleWidth = 430; // panda + speech bubble footprint
      const left = Math.max(20, rect.left - estimatedBubbleWidth - gap);
      const top = Math.max(
        80,
        Math.min(window.innerHeight - 180, rect.top + rect.height / 2 - 70),
      );

      return {
        top: `${top}px`,
        left: `${left}px`,
        right: 'auto',
      };
    }

    // Default position (top-right) for all steps
    return {
      top: '120px',
      right: '150px',
      left: 'auto',
    };
  };

  const speechBubblePosition = getSpeechBubblePosition();

  // Handle click on spotlight area. Do not advance or complete when spotlight target is null.
  const handleSpotlightClick = () => {
    switch (currentTutorialStep) {
      case 4:
        if (!spotlightTarget) return;
        logger.log('🎓 User clicked Create button, navigating and advancing tutorial');
        navigate('/create');
        nextTutorialStep();
        break;
      case 5:
        if (!spotlightTarget) return;
        logger.log('🎓 User clicked Create Character, navigating to character lab');
        navigate('/character-lab');
        nextTutorialStep();
        break;
      case 6:
        if (!showDrawHighlight || !spotlightTarget) return;
        logger.log('🎓 User clicked Draw card');
        spotlightTarget.click();
        nextTutorialStep();
        break;
      case 11:
        if (!spotlightTarget) return;
        logger.log('🎓 User clicked Save button');
        spotlightTarget.click();
        break;
      case 13:
        if (!spotlightTarget) return;
        logger.log('🎓 User clicked Storybooks button, advancing tutorial');
        spotlightTarget.click();
        setTimeout(() => nextTutorialStep(), 500);
        break;
      case 15:
        if (!spotlightTarget) return;
        logger.log('🎓 User clicked Storybooks Create button, advancing tutorial');
        spotlightTarget.click();
        setTimeout(() => nextTutorialStep(), 500);
        break;
      case 16:
        if (!spotlightTarget) return;
        logger.log('🎓 User clicked storybook open target, completing tutorial');
        spotlightTarget.click();
        setTimeout(() => completeTutorial(), 500);
        break;
      default:
        break;
    }
  };

  return (
    <AnimatePresence>
      {showTutorial && (
        <>
          {/* Dark overlay - with spotlight cutout if needed */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[9998] pointer-events-none"
            style={{ fontFamily: "'Fredoka', sans-serif" }}
          >
            {/* Gray overlay background - blocks clicks (not shown on steps 9, 10, 11, 12, and 13) */}
            {![9, 10, 11, 12, 13].includes(currentTutorialStep) && (
              <div className="absolute inset-0 bg-black/60 pointer-events-auto" />
            )}

            {/* Transparent overlay for step 13 - blocks clicks but doesn't darken */}
            {currentTutorialStep === 13 && (
              <div className="absolute inset-0 bg-transparent pointer-events-auto" />
            )}

            {/* Spotlight cutout (white outline) - clickable for some steps */}
            {spotlightStyle && ![8, 9, 12].includes(currentTutorialStep) && (
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{
                  opacity: 1,
                  scale: 1,
                  boxShadow: [
                    '0 0 0 2px white',
                    '0 0 0 6px white',
                    '0 0 0 2px white',
                  ],
                }}
                transition={{
                  boxShadow: {
                    duration: 1.5,
                    repeat: Infinity,
                    ease: 'easeInOut',
                  },
                }}
                onClick={[4, 5, 6, 11, 13, 15, 16].includes(currentTutorialStep) ? handleSpotlightClick : undefined}
                className={`absolute bg-transparent rounded-2xl z-[9999] ${[4, 5, 6, 11, 13, 15, 16].includes(currentTutorialStep) ? 'pointer-events-auto cursor-pointer' : 'pointer-events-none'}`}
                style={{
                  top: `${spotlightStyle.top}px`,
                  left: `${spotlightStyle.left}px`,
                  width: `${spotlightStyle.width}px`,
                  height: `${spotlightStyle.height}px`,
                  boxShadow: '0 0 0 4px white',
                }}
              />
            )}

            {/* Step 8: Spotlight cutouts for 3 tools */}
            {currentTutorialStep === 8 && toolTargets.color && toolTargets.brush && toolTargets.undo && (
              <>
                {/* Color picker spotlight */}
                {(() => {
                  const rect = toolTargets.color.getBoundingClientRect();
                  return (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="absolute bg-transparent rounded-full pointer-events-none z-[9999]"
                      style={{
                        top: `${rect.top - 8}px`,
                        left: `${rect.left - 8}px`,
                        width: `${rect.width + 16}px`,
                        height: `${rect.height + 16}px`,
                        boxShadow: '0 0 0 4px white',
                      }}
                    />
                  );
                })()}

                {/* Brush slider spotlight */}
                {(() => {
                  const rect = toolTargets.brush.getBoundingClientRect();
                  return (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="absolute bg-transparent rounded-2xl pointer-events-none z-[9999]"
                      style={{
                        top: `${rect.top - 8}px`,
                        left: `${rect.left - 8}px`,
                        width: `${rect.width + 16}px`,
                        height: `${rect.height + 16}px`,
                        boxShadow: '0 0 0 4px white',
                      }}
                    />
                  );
                })()}

                {/* Undo/Redo spotlight */}
                {(() => {
                  const rect = toolTargets.undo.getBoundingClientRect();
                  return (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="absolute bg-transparent rounded-2xl pointer-events-none z-[9999]"
                      style={{
                        top: `${rect.top - 8}px`,
                        left: `${rect.left - 8}px`,
                        width: `${rect.width + 16}px`,
                        height: `${rect.height + 16}px`,
                        boxShadow: '0 0 0 4px white',
                      }}
                    />
                  );
                })()}
              </>
            )}
          </motion.div>

          {/* Step 8: Show 3 tool text boxes */}
          {currentTutorialStep === 8 && toolTargets.color && toolTargets.brush && toolTargets.undo && (() => {
            // Calculate a single vertical position for all tooltips (use the toolbar's position)
            const toolbarRect = toolTargets.color.getBoundingClientRect();
            const tooltipBottom = window.innerHeight - toolbarRect.top + 60;

            return (
              <>
                {/* Color picker text box */}
                {(() => {
                  const rect = toolTargets.color.getBoundingClientRect();
                  return (
                    <motion.div
                      key="color-tooltip"
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="fixed z-[10000] pointer-events-none"
                      style={{
                        bottom: `${tooltipBottom}px`,
                        left: `${rect.left + rect.width / 2 - 61}px`,
                        transform: 'translateX(-50%)',
                      }}
                    >
                      <div style={{ background: "#FFFFFF", border: "2px solid #0065F8", borderRadius: 14, boxShadow: "0px 4px 0px #073B8E, 0px 14px 28px rgba(0,101,248,0.20)", padding: "8px 14px", whiteSpace: "nowrap", fontFamily: "'Fredoka', sans-serif" }}>
                        <p style={{ color: "#0B1A33", fontWeight: 700, fontSize: 13, margin: 0 }}>Change colors!</p>
                      </div>
                    </motion.div>
                  );
                })()}

                {/* Brush size text box */}
                {(() => {
                  const rect = toolTargets.brush.getBoundingClientRect();
                  return (
                    <motion.div
                      key="brush-tooltip"
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="fixed z-[10000] pointer-events-none"
                      style={{
                        bottom: `${tooltipBottom}px`,
                        left: `${rect.left + rect.width / 2 - 45}px`,
                        transform: 'translateX(-50%)',
                      }}
                    >
                      <div style={{ background: "#FFFFFF", border: "2px solid #0065F8", borderRadius: 14, boxShadow: "0px 4px 0px #073B8E, 0px 14px 28px rgba(0,101,248,0.20)", padding: "8px 14px", whiteSpace: "nowrap", fontFamily: "'Fredoka', sans-serif" }}>
                        <p style={{ color: "#0B1A33", fontWeight: 700, fontSize: 13, margin: 0 }}>Adjust brush size!</p>
                      </div>
                    </motion.div>
                  );
                })()}

                {/* Undo/Redo text box */}
                {(() => {
                  const rect = toolTargets.undo.getBoundingClientRect();
                  return (
                    <motion.div
                      key="undo-tooltip"
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="fixed z-[10000] pointer-events-none"
                      style={{
                        bottom: `${tooltipBottom}px`,
                        left: `${rect.left + rect.width / 2 - 48}px`,
                        transform: 'translateX(-50%)',
                      }}
                    >
                      <div style={{ background: "#FFFFFF", border: "2px solid #0065F8", borderRadius: 14, boxShadow: "0px 4px 0px #073B8E, 0px 14px 28px rgba(0,101,248,0.20)", padding: "8px 14px", whiteSpace: "nowrap", fontFamily: "'Fredoka', sans-serif" }}>
                        <p style={{ color: "#0B1A33", fontWeight: 700, fontSize: 13, margin: 0 }}>Undo & Redo!</p>
                      </div>
                    </motion.div>
                  );
                })()}
              </>
            );
          })()}

          {/* Speech bubble with Pixley Panda - positioned dynamically based on step */}
          {!content.multiToolStep && (
            <motion.div
              initial={{ opacity: 0, x: 20, y: -20 }}
              animate={{ opacity: 1, x: 0, y: 0 }}
              exit={{ opacity: 0, x: 20, y: -20 }}
              className="fixed z-[9999] pointer-events-auto flex items-center gap-2"
              style={{
                maxWidth: '90%',
                ...speechBubblePosition,
              }}
            >
            {/* Pixley Panda - smaller on steps 9, 10, 11, 12, and 13 */}
            <img
              src="/assets/pixley-panda-nobg.png"
              alt="Pixley Panda"
              onError={(e) => { e.target.src = "/assets/panda-filled.png"; }}
              style={{
                width: [9, 10, 11, 12, 13, 14, 15, 16].includes(currentTutorialStep) ? '120px' : '220px',
                height: [9, 10, 11, 12, 13, 14, 15, 16].includes(currentTutorialStep) ? '120px' : '220px',
                filter: "drop-shadow(0px 8px 20px rgba(11,26,51,0.25))",
              }}
            />

            {/* Speech bubble with tail */}
            <div className="relative">
              {/* Outer tail (blue border) */}
              <div
                className="absolute left-0 top-1/2 -translate-x-6 -translate-y-1/2 w-0 h-0"
                style={{
                  borderTop: '20px solid transparent',
                  borderBottom: '20px solid transparent',
                  borderRight: '24px solid #0065F8',
                }}
              />
              {/* Inner tail (white fill) */}
              <div
                className="absolute left-0 top-1/2 -translate-x-4 -translate-y-1/2 w-0 h-0"
                style={{
                  borderTop: '17px solid transparent',
                  borderBottom: '17px solid transparent',
                  borderRight: '20px solid #FFFFFF',
                }}
              />

              {/* Speech bubble content */}
              <div
                style={{
                  background: "linear-gradient(180deg, #FFFFFF 0%, #F5FAFF 100%)",
                  border: "3px solid #0065F8",
                  borderRadius: 24,
                  boxShadow: "0px 5px 0px #073B8E, 0px 18px 36px rgba(0,101,248,0.22), 0px 24px 64px rgba(11,26,51,0.18)",
                  padding: 20,
                  maxWidth: [9, 10, 11, 12, 13, 14, 15, 16].includes(currentTutorialStep) ? 280 : 380,
                  fontFamily: "'Fredoka', sans-serif",
                }}
              >
                <div className="mb-3">
                  <span style={{ fontFamily: "'Fredoka', sans-serif", color: "#0065F8", fontSize: 10, fontWeight: 800, letterSpacing: "0.18em", textTransform: "uppercase" }}>
                    Tutorial
                  </span>
                  <h3
                    style={{
                      fontFamily: "'Fredoka', sans-serif",
                      color: "#0B1A33",
                      fontWeight: 700,
                      fontSize: [9, 10, 11, 12, 13, 14, 15, 16].includes(currentTutorialStep) ? 15 : 19,
                      textTransform: "uppercase",
                      letterSpacing: "-0.01em",
                      lineHeight: 1.1,
                      margin: "4px 0 6px",
                    }}
                  >
                    {content.title}
                  </h3>
                  <p
                    style={{
                      fontFamily: "'Fredoka', sans-serif",
                      color: "#5A6B85",
                      fontSize: [9, 10, 11, 12, 13, 14, 15, 16].includes(currentTutorialStep) ? 13 : 14,
                      lineHeight: 1.5,
                      margin: 0,
                    }}
                  >
                    {content.message}
                  </p>
                </div>

                {/* Character preview for step 2 */}
                {content.character && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="flex justify-center"
                    style={{ marginBottom: 20 }}
                  >
                    <img
                      src={content.character.animated_image_url || content.character.handdrawn_image_url || content.character.animated_image || content.character.handdrawn_image || '/assets/panda-filled.png'}
                      alt={content.character.name}
                      style={{
                        width: 156, height: 156,
                        borderRadius: 22,
                        objectFit: "cover",
                        border: "4px solid #FFFFFF",
                        boxShadow: "0px 4px 0px rgba(11,26,51,0.10), 0px 0px 0px 4px rgba(0,101,248,0.20), 0px 14px 28px rgba(11,26,51,0.15)",
                      }}
                      onError={(e) => {
                        console.log('❌ Failed to load character image:', e.target.src);
                        e.target.src = '/assets/panda-filled.png';
                      }}
                    />
                  </motion.div>
                )}

                {/* Action buttons */}
                <div className="flex gap-2 justify-end">
                  {content.showSkip && (
                    <button
                      onClick={skipTutorial}
                      style={{
                        fontFamily: "'Fredoka', sans-serif",
                        background: "#FFFFFF",
                        color: "#5A6B85",
                        fontWeight: 700,
                        fontSize: 12,
                        border: "2px solid rgba(11,26,51,0.10)",
                        borderRadius: 999,
                        padding: "8px 16px",
                        boxShadow: "0px 2px 0px rgba(11,26,51,0.05)",
                        cursor: "pointer",
                      }}
                    >
                      Skip Tutorial
                    </button>
                  )}
                  {content.showNext && (
                    <button
                      onClick={isLastTutorialStep ? completeTutorial : nextTutorialStep}
                      className="inline-flex items-center gap-1.5"
                      style={{
                        fontFamily: "'Fredoka', sans-serif",
                        background: "#0065F8",
                        color: "#FFFFFF",
                        fontWeight: 700,
                        fontSize: 13,
                        border: "none",
                        borderRadius: 999,
                        padding: "9px 18px",
                        boxShadow: "0px 3px 0px #073B8E, 0px 10px 22px rgba(0,101,248,0.22)",
                        cursor: "pointer",
                      }}
                    >
                      {isLastTutorialStep ? 'Finish' : 'Next'}
                      <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2.8" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  )}
                  {content.showComplete && (
                    <button
                      onClick={completeTutorial}
                      className="inline-flex items-center gap-1.5"
                      style={{
                        fontFamily: "'Fredoka', sans-serif",
                        background: "#0065F8",
                        color: "#FFFFFF",
                        fontWeight: 700,
                        fontSize: 13,
                        border: "none",
                        borderRadius: 999,
                        padding: "9px 18px",
                        boxShadow: "0px 3px 0px #073B8E, 0px 10px 22px rgba(0,101,248,0.22)",
                        cursor: "pointer",
                      }}
                    >
                      Finish Tutorial
                      <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                    </button>
                  )}
                </div>
              </div>
            </div>
          </motion.div>
          )}

          {/* Step 8: Floating Next button */}
          {currentTutorialStep === 8 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="fixed z-[9999] pointer-events-auto"
              style={{
                bottom: '100px',
                right: '40px',
              }}
            >
              <div className="flex gap-2">
                <button
                  onClick={skipTutorial}
                  style={{
                    fontFamily: "'Fredoka', sans-serif",
                    background: "#FFFFFF",
                    color: "#5A6B85",
                    fontWeight: 700,
                    fontSize: 13,
                    border: "2px solid rgba(11,26,51,0.10)",
                    borderRadius: 999,
                    padding: "9px 18px",
                    boxShadow: "0px 2px 0px rgba(11,26,51,0.05)",
                    cursor: "pointer",
                  }}
                >
                  Skip Tutorial
                </button>
                <button
                  onClick={nextTutorialStep}
                  className="inline-flex items-center gap-1.5"
                  style={{
                    fontFamily: "'Fredoka', sans-serif",
                    background: "#0065F8",
                    color: "#FFFFFF",
                    fontWeight: 700,
                    fontSize: 13,
                    border: "none",
                    borderRadius: 999,
                    padding: "9px 18px",
                    boxShadow: "0px 3px 0px #073B8E, 0px 10px 22px rgba(0,101,248,0.22)",
                    cursor: "pointer",
                  }}
                >
                  Next
                  <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2.8" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
            </motion.div>
          )}
        </>
      )}
    </AnimatePresence>
  );
};

export default Tutorial;
